import React from 'react'
import { useIntl } from 'react-intl'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { Box, useTheme, Button, IconButton } from '@mui/material'
import DndIcon from '../../../../assets/icons/Dnd.svg'
import EditIcon from '../../../../assets/icons/Edit/Edit.svg'
import LockIcon from '../../../../assets/icons/Lock.svg'
import OpenLockIcon from '../../../../assets/icons/OpenLock.svg'
import DocumentIcon from '../../../../assets/icons/Documents/Document.svg'
import VideoLessonIcon from '../../../../assets/icons/VideoLesson.svg'
import ConfirmDelete from '../../../../components/dialogs/ConfirmDelete'
import { ui } from '../../../../utils/uiTokens'

interface ItemProps {
    id?: number
    title?: string
    type?: string
    isReady?: boolean
    countQuestions?: number
}

interface DndListProps {
    items: any[]
    editDnd: boolean
    setItems?: (elements: any[]) => void
    addQuestions?: (element: any) => void
    setEditRow?: (element: any) => void
    onDelete?: (element: any) => void
}

const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list)
    const [removed] = result.splice(startIndex, 1)

    result.splice(endIndex, 0, removed)

    return result
}

function Item({
    item,
    index,
    editDnd,
    addQuestions,
    setEditRow,
    onDelete,
}: {
    item: ItemProps
    index: number
    editDnd: boolean
    addQuestions: (element: any) => void
    setEditRow: (element: any) => void
    onDelete: (element: any) => void
}) {
    const theme = useTheme()
    const { formatMessage } = useIntl()
    const styles = (snapshot) => {
        return {
            icon: {
                marginRight: '16px',
                padding: '4px',
                borderRadius: '50%',
                background: snapshot.isDragging
                    ? theme.palette.grey[100]
                    : ui.card,
                '&:hover': {
                    background: theme.palette.grey[100],
                },
                visibility: editDnd ? 'visible' : 'hidden',
                width: editDnd ? 'auto' : '0',
            },

            dndItem: {
                background: snapshot.isDragging
                    ? ui.pinkSoft
                    : theme.palette.grey[100],
                p: 2,
                width: 660,
                display: 'flex',
                alignItems: 'center',
                borderRadius: 1,
            },

            iconStyle: {
                marginRight: 16,
                width: 24,
            },

            questionsCount: {
                marginLeft: 'auto',
                pl: 2,
                whiteSpace: 'nowrap',
                color: theme.palette.grey[600],
                fontSize: '14px',
            },

            buttonGroups: {
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                ml: 2,
            },

            addQuestionButton: {
                background: ui.pinkSoft,
                color: ui.ink,
                textTransform: 'none',
                padding: '2 12',
                borderRadius: 3,
            },

            iconButtonBackground: {
                background: ui.track,
            },

            boxContainer: {
                display: 'flex',
                alignItems: 'center',
                mb: 2,
            },
        }
    }

    return (
        <Draggable
            draggableId={item.id + item.type}
            index={index}
        >
            {(provided, snapshot) => (
                <Box
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    sx={styles(snapshot).boxContainer}
                >
                    <Box
                        {...provided.dragHandleProps}
                        sx={styles(snapshot).icon}
                    >
                        <img
                            src={DndIcon}
                            alt="menu"
                        />
                    </Box>

                    <Box sx={styles(snapshot).dndItem}>
                        <img
                            src={
                                item.type === 'videoLesson'
                                    ? VideoLessonIcon
                                    : DocumentIcon
                            }
                            alt="menu"
                            style={styles(snapshot).iconStyle}
                        />
                        {item.title}

                        {item.type === 'topicTest' && (
                            <Box sx={styles(snapshot).questionsCount}>
                                {formatMessage(
                                    { id: 'questionsCount' },
                                    { count: item.countQuestions || 0 },
                                )}
                            </Box>
                        )}
                    </Box>

                    <Box sx={styles(snapshot).buttonGroups}>
                        {item.type === 'topicTest' && (
                            <Button
                                sx={styles(snapshot).addQuestionButton}
                                startIcon={
                                    <img
                                        src={EditIcon}
                                        alt="edit"
                                    />
                                }
                                onClick={() => addQuestions(item)}
                            >
                                {formatMessage({ id: 'addQuestions' })}
                            </Button>
                        )}

                        {item.isReady ? (
                            <IconButton sx={{}}>
                                <img
                                    src={OpenLockIcon}
                                    alt="open"
                                />
                            </IconButton>
                        ) : (
                            <IconButton
                                sx={styles(snapshot).iconButtonBackground}
                            >
                                <img
                                    src={LockIcon}
                                    alt="lock"
                                />
                            </IconButton>
                        )}

                        <IconButton
                            sx={styles(snapshot).iconButtonBackground}
                            onClick={() => setEditRow(item)}
                        >
                            <img
                                src={EditIcon}
                                alt="edit"
                            />
                        </IconButton>

                        <ConfirmDelete confirmAction={() => onDelete(item)} />
                    </Box>
                </Box>
            )}
        </Draggable>
    )
}

const ItemList = React.memo(function ItemList({
    items,
    editDnd,
    addQuestions,
    setEditRow,
    onDelete,
}: any) {
    return items.map((item: ItemProps, index: number) => (
        <Item
            item={item}
            index={index}
            key={item.id + item.type}
            editDnd={editDnd}
            addQuestions={addQuestions}
            setEditRow={setEditRow}
            onDelete={onDelete}
        />
    ))
})

function DndList({
    items,
    setItems = () => { },
    editDnd,
    addQuestions = () => { },
    setEditRow = () => { },
    onDelete = () => { },
}: DndListProps) {
    function onDragEnd(result) {
        if (!result.destination) {
            return
        }

        if (result.destination.index === result.source.index) {
            return
        }

        const newItems = reorder(
            items,
            result.source.index,
            result.destination.index,
        )

        setItems(newItems)
    }

    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="list">
                {(provided) => (
                    <Box
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                    >
                        <ItemList
                            items={items}
                            editDnd={editDnd}
                            addQuestions={addQuestions}
                            setEditRow={setEditRow}
                            onDelete={onDelete}
                        />
                        {provided.placeholder}
                    </Box>
                )}
            </Droppable>
        </DragDropContext>
    )
}

export default DndList
